// src/pages/BillingPage.tsx
//
// WS-C — Tenant-facing billing page (read-only).
// Overview totals + invoice list + default payment method placeholder.
// Adding/updating a payment method via CardKnox iFields is deferred to
// a follow-up; the "Update payment method" button opens a notice modal.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, AlertCircle, CreditCard,
  FileText, DollarSign, Download, ExternalLink, Info,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { billingApi, type BillingOverview, type InvoiceListRow, type PaymentMethodSummary } from '../services/billingApi';
import Modal from '../components/Modal';
import { fmtLocalTime } from '../lib/formatTime';

interface BillingPageProps {
  onBack?: () => void;
}

type Tab = 'overview' | 'invoices' | 'payment';

export default function BillingPage({ onBack }: BillingPageProps) {
  const { branding } = useAuthContext();
  const [tab, setTab] = useState<Tab>('overview');

  const overviewQ = useQuery({
    queryKey: ['billing', 'overview'],
    queryFn: () => billingApi.getOverview(),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1200px] mx-auto">
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg shadow-amber-500/20">
                  <CreditCard size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">Billing</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'} — invoices &amp; payment method
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => overviewQ.refetch()}
              disabled={overviewQ.isFetching}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={16} className={overviewQ.isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6">
          {overviewQ.isLoading && (
            <Panel><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></Panel>
          )}
          {overviewQ.error && <Panel><ErrorBox err={overviewQ.error} /></Panel>}

          {overviewQ.data && !overviewQ.data.hasBillingSetup && <NoBillingState />}

          {overviewQ.data?.hasBillingSetup && (
            <>
              <div className="flex items-center gap-1 border-b border-slate-700/40 mb-4">
                {(['overview', 'invoices', 'payment'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px capitalize ${
                      tab === t
                        ? 'text-amber-300 border-amber-500'
                        : 'text-slate-500 border-transparent hover:text-slate-300'
                    }`}
                  >
                    {t === 'payment' ? 'Payment method' : t}
                  </button>
                ))}
              </div>

              {tab === 'overview' && <OverviewTab data={overviewQ.data} />}
              {tab === 'invoices' && <InvoicesTab />}
              {tab === 'payment'  && <PaymentTab defaultMethod={overviewQ.data.defaultPaymentMethod} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

// ── Common ────────────────────────────────────────────────────────

function Panel({ children }: { children: React.ReactNode }) {
  return <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5">{children}</div>;
}

function ErrorBox({ err }: { err: unknown }) {
  return (
    <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 flex items-start gap-2">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <div>{err instanceof Error ? err.message : String(err)}</div>
    </div>
  );
}

function NoBillingState() {
  return (
    <Panel>
      <div className="text-center py-8">
        <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-3">
          <Info size={24} className="text-amber-300" />
        </div>
        <div className="text-base font-semibold text-white mb-1">Billing isn't set up yet</div>
        <p className="text-sm text-slate-400 max-w-md mx-auto">
          We haven't linked this tenant to a billing customer record yet. Contact your account
          manager to get this set up.
        </p>
      </div>
    </Panel>
  );
}

const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });

// ── Overview tab ──────────────────────────────────────────────────

function OverviewTab({ data }: { data: BillingOverview }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard icon={<DollarSign size={16} />} label="Total billed" value={fmt.format(data.totalBilled)} />
        <SummaryCard icon={<DollarSign size={16} />} label="Total paid" value={fmt.format(data.totalPaid)} accent="emerald" />
        <SummaryCard
          icon={<DollarSign size={16} />}
          label="Outstanding"
          value={fmt.format(data.outstanding)}
          accent={data.outstanding > 0 ? 'amber' : 'slate'}
        />
      </div>

      <Panel>
        <div className="flex items-center gap-2 mb-3">
          <FileText size={14} className="text-amber-300" />
          <h3 className="text-sm font-semibold text-white">Recent invoices</h3>
          <span className="text-xs text-slate-500 ml-auto">last {data.recent.length}</span>
        </div>
        {data.recent.length === 0 ? (
          <div className="text-sm text-slate-500 py-3">No invoices yet.</div>
        ) : (
          <InvoiceTable rows={data.recent} />
        )}
      </Panel>
    </div>
  );
}

function SummaryCard({
  icon, label, value, accent = 'slate',
}: { icon: React.ReactNode; label: string; value: string; accent?: 'slate' | 'emerald' | 'amber' }) {
  const colors =
    accent === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
    : accent === 'amber' ? 'border-amber-500/30 bg-amber-500/10 text-amber-100'
    : 'border-slate-700/50 bg-slate-900/40 text-slate-200';
  return (
    <div className={`rounded-2xl border ${colors} p-5`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider opacity-70 mb-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

// ── Invoices tab ──────────────────────────────────────────────────

function InvoicesTab() {
  const q = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => billingApi.listInvoices(),
    refetchOnWindowFocus: false,
  });
  if (q.isLoading) return <Panel><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></Panel>;
  if (q.error)     return <Panel><ErrorBox err={q.error} /></Panel>;
  return (
    <Panel>
      {!q.data || q.data.length === 0
        ? <div className="text-sm text-slate-500 py-3">No invoices.</div>
        : <InvoiceTable rows={q.data} />}
    </Panel>
  );
}

function InvoiceTable({ rows }: { rows: InvoiceListRow[] }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
        <tr>
          <th className="text-left py-2 font-medium">Invoice</th>
          <th className="text-left py-2 font-medium">Date</th>
          <th className="text-left py-2 font-medium">Due</th>
          <th className="text-left py-2 font-medium">Description</th>
          <th className="text-right py-2 font-medium">Billed</th>
          <th className="text-right py-2 font-medium">Paid</th>
          <th className="text-right py-2 font-medium">Balance</th>
          <th className="w-12"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-700/20">
            <td className="py-2 font-mono text-xs text-slate-200">
              {r.invoice}
              {r.invoice_type === 'credit_memo' && (
                <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">CR</span>
              )}
            </td>
            <td className="py-2 text-xs text-slate-300">{fmtLocalTime(r.date) || r.date}</td>
            <td className="py-2 text-xs text-slate-500">{r.due_date ? fmtLocalTime(r.due_date) : '—'}</td>
            <td className="py-2 text-slate-300 truncate max-w-[28ch]">{r.description ?? '—'}</td>
            <td className="py-2 text-right text-slate-200 tabular-nums">{fmt.format(Number(r.amountbilled || 0))}</td>
            <td className="py-2 text-right text-emerald-300 tabular-nums">{fmt.format(Number(r.amountpaid || 0))}</td>
            <td className={`py-2 text-right tabular-nums ${Number(r.balance) > 0 ? 'text-amber-300 font-medium' : 'text-slate-500'}`}>
              {fmt.format(Number(r.balance || 0))}
            </td>
            <td className="py-2 text-right">
              {r.pdf_url
                ? <a
                    href={r.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 p-1.5 rounded text-slate-500 hover:text-amber-300 hover:bg-slate-800/60 transition-colors"
                    title="Open PDF"
                  >
                    <Download size={13} />
                  </a>
                : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Payment method tab ────────────────────────────────────────────

function PaymentTab({ defaultMethod }: { defaultMethod: PaymentMethodSummary | null }) {
  const [updating, setUpdating] = useState(false);
  return (
    <Panel>
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={14} className="text-amber-300" />
        <h3 className="text-sm font-semibold text-white">Payment method on file</h3>
      </div>
      {!defaultMethod ? (
        <div className="text-sm text-slate-500 py-3">
          No payment method on file yet.
        </div>
      ) : (
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/40 p-4 flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-200 font-mono">
              {defaultMethod.maskedCardNumber || (defaultMethod.last4 ? `•••• ${defaultMethod.last4}` : '•••• ••••')}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {[defaultMethod.brand, defaultMethod.nameOnCard, defaultMethod.expMonth && defaultMethod.expYear
                ? `exp ${String(defaultMethod.expMonth).padStart(2,'0')}/${defaultMethod.expYear}`
                : null,
              ].filter(Boolean).join(' · ')}
            </div>
            {!defaultMethod.maskedCardNumber && !defaultMethod.last4 && (
              <div className="text-[10px] text-slate-600 mt-2 italic">
                Card details fetched from CardKnox land in a follow-up release.
              </div>
            )}
          </div>
          <button
            onClick={() => setUpdating(true)}
            className="px-3 py-1.5 text-sm rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 transition-colors"
          >
            Update
          </button>
        </div>
      )}
      {!defaultMethod && (
        <div className="mt-4">
          <button
            onClick={() => setUpdating(true)}
            className="px-3 py-1.5 text-sm rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 transition-colors"
          >
            Add a payment method
          </button>
        </div>
      )}

      {updating && (
        <Modal onClose={() => setUpdating(false)} title="Payment method updates" panelClassName="max-w-md">
          <div className="space-y-3">
            <p className="text-sm text-slate-300">
              We're not yet accepting card updates from this page. The secure CardKnox-tokenized
              entry form is in the next release — until then, please:
            </p>
            <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
              <li>Open a support ticket asking to update your payment method, or</li>
              <li>Email <span className="font-mono text-slate-200">billing@jnebg.com</span> directly.</li>
            </ul>
            <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t border-slate-700/40">
              <ExternalLink size={12} /> Card data is processed by CardKnox; we never see your card number.
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setUpdating(false)}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Panel>
  );
}
